import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Upload, X, Percent, GripVertical, BarChart3, AlertTriangle, Monitor, Tablet, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
    { value: 'percent', label: 'Процентна отстъпка' },
    { value: 'fixed_price', label: 'Фиксирана промо цена' },
    { value: 'multi_buy', label: '„2/3 броя за специална цена"' },
    { value: 'quantity_tiers', label: 'Количество / стек оферта (ценови нива)' },
    { value: 'combo', label: 'Комбо / Пакетна оферта' },
    { value: 'third_free', label: '„Купи 2, третият безплатен"' },
    { value: 'second_half', label: '„Втори продукт -50%"' },
    { value: 'buy_x_get_y', label: '„Купи X → Y на специална цена"' },
    { value: 'spend_over', label: '„Похарчи над X € → отстъпка"' },
];

const POSITIONS = [
    { value: 'hero', label: 'Основен Hero банер (слайдер)' },
    { value: 'secondary', label: 'Вторични промо банери' },
    { value: 'after_categories', label: 'Секция след категориите' },
    { value: 'promo_cards', label: 'Малки промо карти' },
    { value: 'none', label: 'Без банер на началната страница' },
];

const TARGETS = [
    { value: 'offer', label: 'Страницата на тази оферта' },
    { value: 'product', label: 'Конкретен продукт' },
    { value: 'category', label: 'Категория' },
    { value: 'brand', label: 'Марка' },
    { value: 'promotions_page', label: 'Всички промоции' },
    { value: 'custom', label: 'Персонализиран вътрешен URL' },
];

const BTN_PRESETS = ['Виж офертата', 'Пазарувай сега', 'Виж промоциите', 'Вземи офертата', 'Разгледай комбото'];

const emptyForm = {
    name: '', short_title: '', description: '', type: 'percent',
    product_ids: [], category_ids: [], subcategories: [], brand_ids: [],
    percent_off: '', promo_price: '', multi_qty: 2, multi_price: '',
    combo_items: [], combo_price: '', max_per_customer: '', allow_qty_change: true,
    buy_product_id: '', get_product_id: '', get_price: '',
    spend_threshold: '', spend_percent: '', tiers: [],
    start_at: '', end_at: '', active: true, show_countdown: false,
    priority: 0, combine_discounts: false,
    banner: { alt: '', title: '', subtitle: '', button_text: 'Виж офертата', show_text_overlay: true },
    target: { type: 'offer', value: '', extra: '' },
    placement: { position: 'hero', order: 0, in_slider: true, homepage_only: true, category_ids: [] },
    seo: { title: '', description: '', slug: '', og_title: '', og_description: '', noindex: false },
};

const toLocal = (iso) => iso ? new Date(iso).toISOString().slice(0, 16) : '';
const toIso = (local) => local ? new Date(local).toISOString() : null;

const ProductPicker = ({ label, selected, onChange, single = false }) => {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [names, setNames] = useState({});
    const timer = useRef(null);

    const search = (text) => {
        setQ(text);
        clearTimeout(timer.current);
        if (!text || text.length < 2) { setResults([]); return; }
        timer.current = setTimeout(async () => {
            try {
                const res = await axios.get(`${API}/products`, { params: { search: text, limit: 8 } });
                setResults(res.data.products || []);
            } catch { /* noop */ }
        }, 350);
    };

    const add = (p) => {
        setNames(prev => ({ ...prev, [p.product_id]: p.name }));
        if (single) onChange([p.product_id]);
        else if (!selected.includes(p.product_id)) onChange([...selected, p.product_id]);
        setQ(''); setResults([]);
    };

    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <Input value={q} onChange={e => search(e.target.value)} placeholder="Търси продукт..." data-testid="promo-product-search" className="mt-1" />
            {results.length > 0 && (
                <div className="border rounded-md mt-1 max-h-48 overflow-auto bg-white dark:bg-slate-800 shadow z-10 relative">
                    {results.map(p => (
                        <button key={p.product_id} type="button" onClick={() => add(p)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-700 flex justify-between">
                            <span className="truncate">{p.name}</span>
                            <span className="text-muted-foreground ml-2">{p.price?.toFixed(2)} €</span>
                        </button>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap gap-1 mt-2">
                {selected.map(pid => (
                    <Badge key={pid} variant="secondary" className="gap-1">
                        {names[pid] || pid}
                        <button type="button" onClick={() => onChange(selected.filter(x => x !== pid))}><X className="w-3 h-3" /></button>
                    </Badge>
                ))}
            </div>
        </div>
    );
};

const BannerUpload = ({ kind, promoId, hasBanner, onUploaded, token }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const url = hasBanner ? `${API}/promotions/${promoId}/banner/${kind}?t=${Date.now()}` : null;

    const upload = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            setUploading(true);
            try {
                await axios.post(`${API}/admin/promotions/${promoId}/banner`, { kind, data: reader.result },
                    { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`${kind === 'desktop' ? 'Desktop' : 'Mobile'} банер качен`);
                onUploaded();
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Грешка при качване');
            } finally { setUploading(false); }
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{kind === 'desktop' ? '🖥 Desktop банер (препоръчително 1600×500)' : '📱 Mobile банер (препоръчително 800×600)'}</span>
                <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`upload-banner-${kind}`}>
                        <Upload className="w-3 h-3 mr-1" />{uploading ? 'Качва се...' : 'Качи'}
                    </Button>
                    {hasBanner && (
                        <Button size="sm" variant="ghost" onClick={async () => {
                            await axios.delete(`${API}/admin/promotions/${promoId}/banner/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
                            onUploaded();
                        }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    )}
                </div>
            </div>
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} />
            {url ? <img src={url} alt="банер" className="w-full rounded max-h-40 object-cover" /> :
                <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded flex items-center justify-center text-xs text-muted-foreground">Няма качен банер</div>}
        </div>
    );
};

const DevicePreview = ({ promo }) => {
    const [device, setDevice] = useState('desktop');
    const hb = promo?.has_banner || {};
    const kind = device === 'mobile' && hb.mobile ? 'mobile' : (hb.desktop ? 'desktop' : (hb.mobile ? 'mobile' : null));
    if (!kind) return null;
    const widths = { desktop: '100%', tablet: '60%', mobile: '32%' };
    const b = promo.banner || {};
    return (
        <div className="border rounded-lg p-3">
            <div className="flex gap-1 mb-2">
                {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([d, Icon]) => (
                    <Button key={d} size="sm" variant={device === d ? 'default' : 'outline'} onClick={() => setDevice(d)} data-testid={`preview-${d}`}>
                        <Icon className="w-3 h-3" />
                    </Button>
                ))}
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 rounded p-3 flex justify-center">
                <div style={{ width: widths[device] }} className="relative rounded-lg overflow-hidden shadow">
                    <img src={`${API}/promotions/${promo.promo_id}/banner/${kind}?t=${Date.now()}`} alt={b.alt || ''} className="w-full object-cover" />
                    {b.show_text_overlay && (b.title || b.button_text) && (
                        <div className="absolute inset-0 bg-black/30 flex flex-col justify-center items-start p-4 sm:p-6">
                            {b.title && <p className="text-white font-bold text-sm sm:text-xl drop-shadow">{b.title}</p>}
                            {b.subtitle && <p className="text-white/90 text-xs sm:text-sm mt-1">{b.subtitle}</p>}
                            {b.button_text && <span className="mt-2 bg-amber-500 text-white text-xs sm:text-sm px-3 py-1.5 rounded-full font-semibold">{b.button_text}</span>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const PromotionsManager = () => {
    const { token } = useAuth();
    const [promos, setPromos] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [conflicts, setConflicts] = useState(null);
    const [statsFor, setStatsFor] = useState(null);
    const dragIdx = useRef(null);

    const load = useCallback(async () => {
        try {
            const [pr, cr, br] = await Promise.all([
                axios.get(`${API}/admin/promotions`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/categories`),
                axios.get(`${API}/brands`).catch(() => ({ data: [] })),
            ]);
            setPromos(pr.data.promotions || []);
            setCategories(cr.data || []);
            setBrands(Array.isArray(br.data) ? br.data : (br.data.brands || []));
        } catch { toast.error('Грешка при зареждане'); }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            ...emptyForm, ...p,
            percent_off: p.percent_off ?? '', promo_price: p.promo_price ?? '', multi_price: p.multi_price ?? '',
            combo_price: p.combo_price ?? '', max_per_customer: p.max_per_customer ?? '', get_price: p.get_price ?? '',
            spend_threshold: p.spend_threshold ?? '', spend_percent: p.spend_percent ?? '',
            start_at: toLocal(p.start_at), end_at: toLocal(p.end_at),
            banner: { ...emptyForm.banner, ...(p.banner || {}) },
            target: { ...emptyForm.target, ...(p.target || {}) },
            placement: { ...emptyForm.placement, ...(p.placement || {}) },
            seo: { ...emptyForm.seo, ...(p.seo || {}) },
        });
        setShowDialog(true);
    };

    const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);

    const save = async (force = false) => {
        if (!form.name.trim()) { toast.error('Въведете име на офертата'); return; }
        setSaving(true);
        const payload = {
            ...form,
            tiers: (form.tiers || []).filter(t => t.min_qty && t.unit_price !== '').map(t => ({ min_qty: Number(t.min_qty), unit_price: Number(t.unit_price) })),
            percent_off: numOrNull(form.percent_off), promo_price: numOrNull(form.promo_price),
            multi_qty: numOrNull(form.multi_qty), multi_price: numOrNull(form.multi_price),
            combo_price: numOrNull(form.combo_price), max_per_customer: numOrNull(form.max_per_customer),
            get_price: numOrNull(form.get_price), spend_threshold: numOrNull(form.spend_threshold),
            spend_percent: numOrNull(form.spend_percent), priority: Number(form.priority) || 0,
            start_at: toIso(form.start_at), end_at: toIso(form.end_at),
            buy_product_id: form.buy_product_id || null, get_product_id: form.get_product_id || null,
        };
        try {
            const url = editing ? `${API}/admin/promotions/${editing.promo_id}` : `${API}/admin/promotions`;
            const method = editing ? 'put' : 'post';
            const res = await axios[method](`${url}?force=${force}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.conflicts) { setConflicts({ list: res.data.conflicts, payload }); setSaving(false); return; }
            toast.success(editing ? 'Промоцията е обновена' : 'Промоцията е създадена');
            if (!editing && res.data.promotion) setEditing(res.data.promotion);
            setConflicts(null);
            await load();
            if (!editing && res.data.promotion) {
                openEdit(res.data.promotion);
                toast.info('Сега качете банери за офертата 👇');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при запис');
        } finally { setSaving(false); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Изтриване на "${p.name}"? Банерите ѝ ще спрат да се показват.`)) return;
        await axios.delete(`${API}/admin/promotions/${p.promo_id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Изтрита');
        load();
    };

    const toggleActive = async (p) => {
        const payload = { ...p, active: !p.active };
        delete payload.is_live; delete payload.stats; delete payload.created_at; delete payload.updated_at; delete payload.has_banner;
        await axios.put(`${API}/admin/promotions/${p.promo_id}?force=true`, payload, { headers: { Authorization: `Bearer ${token}` } });
        load();
    };

    const onDrop = async (idx) => {
        if (dragIdx.current === null || dragIdx.current === idx) return;
        const arr = [...promos];
        const [moved] = arr.splice(dragIdx.current, 1);
        arr.splice(idx, 0, moved);
        setPromos(arr);
        dragIdx.current = null;
        await axios.put(`${API}/admin/promotions-reorder`, { ids: arr.map(p => p.promo_id) }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Подредбата е запазена');
    };

    const showStats = async (p) => {
        const res = await axios.get(`${API}/admin/promotions/${p.promo_id}/stats`, { headers: { Authorization: `Bearer ${token}` } });
        setStatsFor({ name: p.name, ...res.data.stats });
    };

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const setNested = (obj, k, v) => setForm(prev => ({ ...prev, [obj]: { ...prev[obj], [k]: v } }));

    const t = form.type;
        const needsTargeting = ['percent', 'fixed_price', 'multi_buy', 'third_free', 'second_half', 'spend_over', 'quantity_tiers'].includes(t);

    return (
        <div className="p-4 sm:p-6" data-testid="promotions-manager">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2"><Percent className="w-6 h-6 text-amber-600" /> Промоции и оферти</h2>
                    <p className="text-sm text-muted-foreground">Влачете редовете, за да подредите банерите на началната страница</p>
                </div>
                <Button onClick={openNew} data-testid="create-promotion-btn"><Plus className="w-4 h-4 mr-1" /> Нова промоция</Button>
            </div>

            <div className="space-y-2">
                {promos.length === 0 && <p className="text-muted-foreground text-sm">Няма създадени промоции. Създайте първата! 🎉</p>}
                {promos.map((p, idx) => (
                    <div key={p.promo_id} draggable
                        onDragStart={() => { dragIdx.current = idx; }}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => onDrop(idx)}
                        className="flex items-center gap-3 border rounded-lg p-3 bg-white dark:bg-slate-800 cursor-move"
                        data-testid={`promo-row-${p.promo_id}`}>
                        <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                        {(p.has_banner?.desktop || p.has_banner?.mobile) && (
                            <img src={`${API}/promotions/${p.promo_id}/banner/${p.has_banner?.desktop ? 'desktop' : 'mobile'}`} alt="" className="w-16 h-10 object-cover rounded" loading="lazy" />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium truncate">{p.name}</span>
                                <Badge variant="outline" className="text-xs">{TYPES.find(x => x.value === p.type)?.label || p.type}</Badge>
                                {p.is_live ? <Badge className="bg-green-100 text-green-800 text-xs">НА ЖИВО</Badge> :
                                    p.active ? <Badge className="bg-blue-100 text-blue-800 text-xs">Планирана</Badge> :
                                        <Badge variant="secondary" className="text-xs">Неактивна</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {POSITIONS.find(x => x.value === p.placement?.position)?.label} · {p.start_at ? new Date(p.start_at).toLocaleString('bg-BG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'без начало'} → {p.end_at ? new Date(p.end_at).toLocaleString('bg-BG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'без край'}
                            </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`promo-active-${p.promo_id}`} />
                            <Button size="sm" variant="ghost" onClick={() => showStats(p)} title="Статистика"><BarChart3 className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => window.open(`/offer/${p.seo?.slug || p.promo_id}`, '_blank')} title="Преглед като клиент">👁</Button>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-promo-${p.promo_id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(p)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? 'Редакция на промоция' : 'Нова промоция'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label>Име на офертата *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="promo-name-input" /></div>
                            <div><Label>Кратко заглавие</Label><Input value={form.short_title} onChange={e => set('short_title', e.target.value)} /></div>
                        </div>
                        <div><Label>Описание</Label><Textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} /></div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Вид оферта</Label>
                                <Select value={form.type} onValueChange={v => set('type', v)}>
                                    <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{TYPES.map(x => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Приоритет (по-висок печели при конфликт)</Label><Input type="number" value={form.priority} onChange={e => set('priority', e.target.value)} /></div>
                        </div>

                        {/* Mechanics */}
                        <div className="border rounded-lg p-3 space-y-3 bg-amber-50/50 dark:bg-slate-800/50">
                            <p className="text-sm font-semibold">Механика на офертата</p>
                            {t === 'percent' && <div><Label>Процент отстъпка (%)</Label><Input type="number" value={form.percent_off} onChange={e => set('percent_off', e.target.value)} data-testid="promo-percent-input" /></div>}
                            {t === 'fixed_price' && <div><Label>Промоционална цена (€)</Label><Input type="number" step="0.01" value={form.promo_price} onChange={e => set('promo_price', e.target.value)} /></div>}
                            {t === 'multi_buy' && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Брой продукти</Label><Input type="number" value={form.multi_qty} onChange={e => set('multi_qty', e.target.value)} /></div>
                                    <div><Label>Обща цена за броя (€)</Label><Input type="number" step="0.01" value={form.multi_price} onChange={e => set('multi_price', e.target.value)} /></div>
                                </div>
                            )}
                            {t === 'combo' && (
                                <div className="space-y-3">
                                    <ProductPicker label="Добави продукт към комбото" selected={form.combo_items.map(i => i.product_id)}
                                        onChange={(ids) => set('combo_items', ids.map(pid => form.combo_items.find(i => i.product_id === pid) || { product_id: pid, quantity: 1 }))} />
                                    {form.combo_items.map((it, i) => (
                                        <div key={it.product_id} className="flex items-center gap-2 text-sm">
                                            <span className="flex-1 truncate">{it.product_id}</span>
                                            <Label className="text-xs">Кол.</Label>
                                            <Input type="number" min="1" className="w-20" value={it.quantity}
                                                onChange={e => { const arr = [...form.combo_items]; arr[i] = { ...it, quantity: Number(e.target.value) || 1 }; set('combo_items', arr); }} />
                                        </div>
                                    ))}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label>Комбо цена (€)</Label><Input type="number" step="0.01" value={form.combo_price} onChange={e => set('combo_price', e.target.value)} data-testid="combo-price-input" /></div>
                                        <div><Label>Макс. комбота на клиент</Label><Input type="number" value={form.max_per_customer} onChange={e => set('max_per_customer', e.target.value)} placeholder="без лимит" /></div>
                                    </div>
                                    <div className="flex items-center gap-2"><Switch checked={form.allow_qty_change} onCheckedChange={v => set('allow_qty_change', v)} /><Label className="text-xs">Клиентът може да променя количествата в количката</Label></div>
                                </div>
                            )}
                            {t === 'quantity_tiers' && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">Ценови нива: при достигане на количеството ЦЯЛОТО количество се таксува по цената на нивото. Пример: 5+ стека → 15 € на стек.</p>
                                    {form.tiers.map((tier, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <Label className="text-xs whitespace-nowrap">От</Label>
                                            <Input type="number" min="2" className="w-20" value={tier.min_qty}
                                                onChange={e => { const arr = [...form.tiers]; arr[i] = { ...tier, min_qty: Number(e.target.value) || 2 }; set('tiers', arr); }}
                                                data-testid={`tier-qty-${i}`} />
                                            <Label className="text-xs whitespace-nowrap">броя → цена на брой (€)</Label>
                                            <Input type="number" step="0.01" className="w-24" value={tier.unit_price}
                                                onChange={e => { const arr = [...form.tiers]; arr[i] = { ...tier, unit_price: e.target.value }; set('tiers', arr); }}
                                                data-testid={`tier-price-${i}`} />
                                            <Button type="button" size="sm" variant="ghost" onClick={() => set('tiers', form.tiers.filter((_, j) => j !== i))}><X className="w-3 h-3 text-red-500" /></Button>
                                        </div>
                                    ))}
                                    <Button type="button" size="sm" variant="outline" onClick={() => set('tiers', [...form.tiers, { min_qty: form.tiers.length ? Number(form.tiers[form.tiers.length - 1].min_qty) + 1 : 2, unit_price: '' }])} data-testid="add-tier-btn">
                                        <Plus className="w-3 h-3 mr-1" /> Добави ниво
                                    </Button>
                                </div>
                            )}
                            {t === 'buy_x_get_y' && (
                                <div className="space-y-3">
                                    <ProductPicker label="Купи продукт (X)" single selected={form.buy_product_id ? [form.buy_product_id] : []} onChange={ids => set('buy_product_id', ids[0] || '')} />
                                    <ProductPicker label="Получи продукт (Y)" single selected={form.get_product_id ? [form.get_product_id] : []} onChange={ids => set('get_product_id', ids[0] || '')} />
                                    <div><Label>Специална цена за Y (€) — 0 = безплатен</Label><Input type="number" step="0.01" value={form.get_price} onChange={e => set('get_price', e.target.value)} /></div>
                                </div>
                            )}
                            {t === 'spend_over' && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Минимална сума (€)</Label><Input type="number" step="0.01" value={form.spend_threshold} onChange={e => set('spend_threshold', e.target.value)} /></div>
                                    <div><Label>Отстъпка (%)</Label><Input type="number" value={form.spend_percent} onChange={e => set('spend_percent', e.target.value)} /></div>
                                </div>
                            )}
                            {['third_free', 'second_half'].includes(t) && <p className="text-xs text-muted-foreground">Правилото се прилага автоматично за избраните продукти по-долу.</p>}
                        </div>

                        {/* Targeting */}
                        {needsTargeting && (
                            <div className="border rounded-lg p-3 space-y-3">
                                <p className="text-sm font-semibold">Кои продукти участват</p>
                                <ProductPicker label="Конкретни продукти" selected={form.product_ids} onChange={v => set('product_ids', v)} />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Категории</Label>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {categories.map(c => (
                                                <Badge key={c.category_id} onClick={() => set('category_ids', form.category_ids.includes(c.category_id) ? form.category_ids.filter(x => x !== c.category_id) : [...form.category_ids, c.category_id])}
                                                    className={`cursor-pointer ${form.category_ids.includes(c.category_id) ? 'bg-amber-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>{c.name}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Марки</Label>
                                        <div className="flex flex-wrap gap-1 mt-1 max-h-28 overflow-auto">
                                            {brands.map(b => (
                                                <Badge key={b.brand_id} onClick={() => set('brand_ids', form.brand_ids.includes(b.brand_id) ? form.brand_ids.filter(x => x !== b.brand_id) : [...form.brand_ids, b.brand_id])}
                                                    className={`cursor-pointer ${form.brand_ids.includes(b.brand_id) ? 'bg-amber-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>{b.name}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Schedule */}
                        <div className="border rounded-lg p-3 space-y-3">
                            <p className="text-sm font-semibold">График (автоматично стартиране и приключване)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Начало (дата и час)</Label><Input type="datetime-local" value={form.start_at} onChange={e => set('start_at', e.target.value)} data-testid="promo-start-input" /></div>
                                <div><Label>Край (дата и час)</Label><Input type="datetime-local" value={form.end_at} onChange={e => set('end_at', e.target.value)} data-testid="promo-end-input" /></div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => set('active', v)} data-testid="promo-active-switch" /><Label>Активна</Label></div>
                                <div className="flex items-center gap-2"><Switch checked={form.show_countdown} onCheckedChange={v => set('show_countdown', v)} /><Label>Показвай countdown таймер</Label></div>
                            </div>
                        </div>

                        {/* Banner texts */}
                        <div className="border rounded-lg p-3 space-y-3">
                            <p className="text-sm font-semibold">Банер — текстове</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div><Label>Заглавие върху банера</Label><Input value={form.banner.title} onChange={e => setNested('banner', 'title', e.target.value)} /></div>
                                <div><Label>Допълнителен текст</Label><Input value={form.banner.subtitle} onChange={e => setNested('banner', 'subtitle', e.target.value)} /></div>
                                <div><Label>Алтернативен текст (alt)</Label><Input value={form.banner.alt} onChange={e => setNested('banner', 'alt', e.target.value)} /></div>
                                <div>
                                    <Label>Текст на бутона</Label>
                                    <Input value={form.banner.button_text} onChange={e => setNested('banner', 'button_text', e.target.value)} list="btn-presets" />
                                    <datalist id="btn-presets">{BTN_PRESETS.map(x => <option key={x} value={x} />)}</datalist>
                                </div>
                            </div>
                            <div className="flex items-center gap-2"><Switch checked={form.banner.show_text_overlay} onCheckedChange={v => setNested('banner', 'show_text_overlay', v)} /><Label className="text-xs">Показвай текста върху снимката (изключи за чисто изображение)</Label></div>
                        </div>

                        {/* Target link */}
                        <div className="border rounded-lg p-3 space-y-3">
                            <p className="text-sm font-semibold">Къде води този банер?</p>
                            <Select value={form.target.type} onValueChange={v => setNested('target', 'type', v)}>
                                <SelectTrigger data-testid="promo-target-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{TARGETS.map(x => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                            </Select>
                            {form.target.type === 'product' && <ProductPicker label="Продукт" single selected={form.target.value ? [form.target.value] : []} onChange={ids => setNested('target', 'value', ids[0] || '')} />}
                            {form.target.type === 'category' && (
                                <Select value={form.target.value} onValueChange={v => setNested('target', 'value', v)}>
                                    <SelectTrigger><SelectValue placeholder="Избери категория" /></SelectTrigger>
                                    <SelectContent>{categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                                </Select>
                            )}
                            {form.target.type === 'brand' && (
                                <Select value={form.target.value} onValueChange={v => setNested('target', 'value', v)}>
                                    <SelectTrigger><SelectValue placeholder="Избери марка" /></SelectTrigger>
                                    <SelectContent>{brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}</SelectContent>
                                </Select>
                            )}
                            {form.target.type === 'custom' && <div><Label>Вътрешен URL (започва с /)</Label><Input value={form.target.value} onChange={e => setNested('target', 'value', e.target.value)} placeholder="/products?category=..." /></div>}
                        </div>

                        {/* Placement */}
                        <div className="border rounded-lg p-3 space-y-3">
                            <p className="text-sm font-semibold">Позиция на началната страница</p>
                            <Select value={form.placement.position} onValueChange={v => setNested('placement', 'position', v)}>
                                <SelectTrigger data-testid="promo-position-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{POSITIONS.map(x => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <div className="flex items-center gap-2"><Switch checked={form.placement.in_slider} onCheckedChange={v => setNested('placement', 'in_slider', v)} /><Label className="text-xs">Част от слайдер (превърта се автоматично)</Label></div>
                        </div>

                        {/* SEO */}
                        <div className="border rounded-lg p-3 space-y-3">
                            <p className="text-sm font-semibold">SEO (по избор)</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div><Label>SEO заглавие</Label><Input value={form.seo.title} onChange={e => setNested('seo', 'title', e.target.value)} /></div>
                                <div><Label>Slug (URL)</Label><Input value={form.seo.slug} onChange={e => setNested('seo', 'slug', e.target.value)} placeholder="lyatna-promocia" /></div>
                            </div>
                            <div><Label>Meta описание</Label><Textarea rows={2} value={form.seo.description} onChange={e => setNested('seo', 'description', e.target.value)} /></div>
                            <div className="flex items-center gap-2"><Switch checked={form.seo.noindex} onCheckedChange={v => setNested('seo', 'noindex', v)} /><Label className="text-xs">Не индексирай (noindex)</Label></div>
                        </div>

                        {/* Banners upload (only after created) */}
                        {editing && (
                            <div className="space-y-3">
                                <p className="text-sm font-semibold">Банери</p>
                                <BannerUpload kind="desktop" promoId={editing.promo_id} hasBanner={editing.has_banner?.desktop} token={token} onUploaded={async () => { await load(); const fresh = (await axios.get(`${API}/admin/promotions`, { headers: { Authorization: `Bearer ${token}` } })).data.promotions.find(x => x.promo_id === editing.promo_id); if (fresh) setEditing(fresh); }} />
                                <BannerUpload kind="mobile" promoId={editing.promo_id} hasBanner={editing.has_banner?.mobile} token={token} onUploaded={async () => { const fresh = (await axios.get(`${API}/admin/promotions`, { headers: { Authorization: `Bearer ${token}` } })).data.promotions.find(x => x.promo_id === editing.promo_id); if (fresh) setEditing(fresh); await load(); }} />
                                <DevicePreview promo={{ ...editing, banner: form.banner }} />
                            </div>
                        )}
                        {!editing && <p className="text-xs text-muted-foreground">💡 Първо запазете промоцията — след това ще можете да качите desktop и mobile банери с преглед.</p>}

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setShowDialog(false)}>Затвори</Button>
                            <Button onClick={() => save(false)} disabled={saving} data-testid="save-promotion-btn">{saving ? 'Запазва се...' : 'Запази промоцията'}</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Conflicts dialog */}
            <Dialog open={!!conflicts} onOpenChange={() => setConflicts(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Конфликт на промоции</DialogTitle></DialogHeader>
                    <p className="text-sm">Някои продукти вече участват в друга активна промоция за същия период:</p>
                    <div className="space-y-2 max-h-48 overflow-auto">
                        {conflicts?.list?.map(c => (
                            <div key={c.promo_id} className="border rounded p-2 text-sm">
                                <p className="font-medium">{c.name}</p>
                                <p className="text-xs text-muted-foreground">{c.count} общи продукта: {c.products.join(', ')}</p>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground">При запазване промоцията с по-висок приоритет ще се приложи. Отстъпките НЕ се комбинират автоматично.</p>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setConflicts(null)}>Откажи (запази съществуващата)</Button>
                        <Button onClick={() => save(true)} data-testid="force-save-promo-btn">Запази новата въпреки това</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Stats dialog */}
            <Dialog open={!!statsFor} onOpenChange={() => setStatsFor(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>📊 Статистика: {statsFor?.name}</DialogTitle></DialogHeader>
                    {statsFor && (
                        <div className="grid grid-cols-2 gap-3">
                            {[['Показвания на банера', statsFor.views], ['Кликове', statsFor.clicks], ['CTR', `${statsFor.ctr}%`],
                              ['Добавяния в количка', statsFor.add_to_cart], ['Поръчки', statsFor.orders], ['Продадени количества', statsFor.qty_sold],
                              ['Оборот', `${(statsFor.revenue || 0).toFixed(2)} €`], ['Дадена отстъпка', `${(statsFor.discount_given || 0).toFixed(2)} €`]].map(([l, v]) => (
                                <div key={l} className="border rounded p-3 text-center">
                                    <p className="text-2xl font-bold text-amber-600">{v ?? 0}</p>
                                    <p className="text-xs text-muted-foreground">{l}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PromotionsManager;
